import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/config";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Loader2, X, CheckCircle2, Plus, Leaf, Sparkles, ArrowRight, ArrowLeft } from "lucide-react";
import AdvancedAddonForm, { ADVANCED_ADDON_KEYS } from "./AdvancedAddonForm";
import FrequencySchedulePicker from "./FrequencySchedulePicker";
import AreaSelector, { areaMultiplierFor } from "./AreaSelector";

const RANGE_KEYS = new Set(["hedge_trimming", "yard_cleanup", "mulching", "landscaping"]);

// Range-service input presets so the dialog can compute a midpoint price
// without dragging the full QuoteRangeBuilder UI into the booking flow.
const RANGE_PRESETS = {
  hedge_trimming: { bush_count: 5, bush_size: "medium", bush_haul_away: "none", label: "5 medium bushes" },
  yard_cleanup:   { cleanup_intensity: "standard", cleanup_sqft: 2000, label: "Standard cleanup · 2,000 sqft" },
  mulching:       { mulch_bed_tier: "medium", label: "Medium beds" },
  landscaping:    { project_size: "small", label: "Small project" },
};

const QUICK_SQFT_BUTTONS = [1000, 2500, 5000];

// iter238 — sensible per-service default sqft so non-lawn add-ons don't
// inherit the primary lawn service's (usually large) area and open at a
// runaway price. Anything not listed falls back to the QUICK_SQFT_BUTTONS
// midpoint (2500).
const SERVICE_DEFAULT_SQFT = {
  lawn_mowing: 2500,
  pressure_washing: 1000,
  sodding: 1000,
  weed_control: 2000,
  fertilization: 2000,
  aeration: 2000,
  edging: 500,
};
const defaultSqftFor = (key) => SERVICE_DEFAULT_SQFT[key] || 2500;

export default function AddServiceDialog({
  open,
  onClose,
  onAdd,            // (item) => void
  services = [],
  excludeKeys = [], // primary service + already-added
  defaultSqft = 2000,
  defaultStartDate = "",  // iter239 — anchor addon schedule to main booking date
}) {
  const [step, setStep] = useState("pick");   // "pick" | "schedule" | "config"
  const [selectedKey, setSelectedKey] = useState(null);
  const [sqft, setSqft] = useState(defaultSqft);
  const [loadingQuote, setLoadingQuote] = useState(false);
  const [quote, setQuote] = useState(null);   // { price, label, low?, high?, is_range }
  // iter126 — shared area selection (empty forces customer to pick on the map).
  const [areas, setAreas] = useState([]);
  // iter108/iter239 — per-service schedule captured in Step 2 (before config).
  // start_date defaults to the primary booking's preferred_date so a one-time
  // addon is scheduled on the SAME visit, not today.
  const [schedule, setSchedule] = useState({
    frequency: "one_time",
    start_date: defaultStartDate || null,
    arrival_window: "morning",
    end_date: null,
    continue_until_cancelled: true,
    custom_schedule_mode: null,
    custom_schedule_value: null,
  });

  // Hide already-chosen services + the primary
  const available = useMemo(
    () => services.filter((s) => !excludeKeys.includes(s.key)),
    [services, excludeKeys]
  );

  useEffect(() => {
    if (!open) {
      // reset
      setStep("pick"); setSelectedKey(null); setQuote(null); setSqft(defaultSqft); setLoadingQuote(false); setAdvancedQuote(null);
      setAreas([]);  // iter126 — reset area selection
      setSchedule({
        frequency: "one_time", start_date: defaultStartDate || null, arrival_window: "morning",
        end_date: null, continue_until_cancelled: true,
        custom_schedule_mode: null, custom_schedule_value: null,
      });
    }
  }, [open, defaultSqft, defaultStartDate]);

  const selected = useMemo(
    () => services.find((s) => s.key === selectedKey),
    [services, selectedKey]
  );
  const isAdvanced = selectedKey ? ADVANCED_ADDON_KEYS.has(selectedKey) : false;
  const isRange = selectedKey && !isAdvanced ? RANGE_KEYS.has(selectedKey) : false;

  // Advanced-form quote (from AdvancedAddonForm's onQuoteChange)
  const [advancedQuote, setAdvancedQuote] = useState(null);

  // Recompute price whenever sqft/service/areas changes (non-advanced path only)
  useEffect(() => {
    if (step !== "config" || !selectedKey || isAdvanced) return;
    // iter126 — customer must pick at least one area before we call the engine.
    if (!areas.length) { setQuote(null); return; }
    let active = true;
    setLoadingQuote(true);
    (async () => {
      try {
        if (isRange) {
          const preset = RANGE_PRESETS[selectedKey] || {};
          const { data } = await api.post("/quotes/range", { service_key: selectedKey, ...preset });
          if (active) setQuote({
            price: data.midpoint || ((data.low + data.high) / 2),
            label: preset.label || selected?.name || "",
            low: data.low,
            high: data.high,
            is_range: true,
          });
        } else {
          const { data } = await api.post("/estimates/calculate", {
            service_key: selectedKey,
            square_feet: sqft,
            frequency: "one_time",
            yard_area: "both",
            areas,  // iter126 — multi-area drives labor multiplier
            area_already_scaled: false,
          });
          if (active) setQuote({
            price: data.total,
            label: (data.areas_label || `${sqft.toLocaleString()} sqft`),
            is_range: false,
            areas_label: data.areas_label,
            areas_price_affecting: data.areas_price_affecting,
          });
        }
      } catch (e) {
        if (active) setQuote(null);
      } finally {
        if (active) setLoadingQuote(false);
      }
    })();
    return () => { active = false; };
  }, [step, selectedKey, sqft, isRange, isAdvanced, selected, areas]);

  if (!open) return null;

  const handleSelect = (key) => {
    // iter238 — Every add-on gets its OWN sensible default sqft
    // rather than inheriting the primary lawn's (often large) area,
    // so e.g. Pressure Washing doesn't open at $1,449 by default.
    setSqft(defaultSqftFor(key));
    setSelectedKey(key);
    setStep("schedule"); // iter108 — schedule BEFORE config, per user spec
  };

  const handleAdd = () => {
    // iter107/108/126 — Advanced addons carry an itemized breakdown + a full
    // per-service schedule. Their `options.areas` comes from the shared
    // AreaSelector step 0 of the AdvancedAddonForm wizard.
    if (isAdvanced) {
      if (!advancedQuote) return;
      const adv_areas = advancedQuote.options?.areas || [];
      onAdd({
        service_key: selectedKey,
        service_name: advancedQuote.service_name || selected?.name || selectedKey,
        square_feet: 0,
        frequency: schedule.frequency || "one_time",
        yard_area: advancedQuote.options?.yard_area || "both",
        areas: adv_areas,  // iter126 — persist canonical area list
        price: Number(advancedQuote.price.toFixed(2)),
        label: advancedQuote.summary_label || "",
        is_range: false,
        is_advanced: true,
        options: advancedQuote.options || {},
        line_items: advancedQuote.line_items || [],
        photo_media_ids: advancedQuote.photo_media_ids || [],
        schedule,
      });
      onClose?.();
      return;
    }
    if (!quote || !selectedKey) return;
    onAdd({
      service_key: selectedKey,
      service_name: selected?.name || selectedKey,
      square_feet: isRange ? 0 : sqft,
      frequency: schedule.frequency || "one_time",
      yard_area: "both",
      areas,  // iter126 — persist canonical area list
      price: Number(quote.price.toFixed(2)),
      quote_low: quote.low || null,
      quote_high: quote.high || null,
      label: quote.label || "",
      is_range: isRange,
      schedule,
    });
    onClose?.();
  };

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/55 flex items-end sm:items-center justify-center p-3"
      onClick={onClose}
      data-testid="add-service-dialog"
    >
      <div
        className="bg-white w-full max-w-lg rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[88vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-br from-[#062818] to-[#0B4029] text-white px-5 py-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
            <Plus className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] font-extrabold text-emerald-200/90">
              {step === "pick" ? "Pick a service" : step === "schedule" ? "Step 1 · Frequency & schedule" : "Step 2 · Configure & price"}
            </div>
            <div className="text-lg font-extrabold tracking-tight font-[Outfit]">
              {step === "pick" ? "Pick a service" : selected?.name}
            </div>
          </div>
          <button onClick={onClose} aria-label="Close" className="w-9 h-9 rounded-full hover:bg-white/10 flex items-center justify-center">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        {step === "pick" ? (
          <div className="overflow-y-auto p-3" data-testid="add-service-list">
            {available.length === 0 ? (
              <div className="text-center text-sm text-gray-500 py-10">All services already added.</div>
            ) : (
              <ul className="grid grid-cols-2 gap-2">
                {available.map((s) => (
                  <li key={s.key}>
                    <button
                      type="button"
                      onClick={() => handleSelect(s.key)}
                      data-testid={`add-service-${s.key}`}
                      className="w-full text-left rounded-2xl border border-gray-200 bg-white p-3 hover:border-emerald-400 hover:bg-emerald-50/40 transition active:scale-[0.98]"
                    >
                      <div className="flex items-center gap-2">
                        <span className="w-9 h-9 rounded-xl bg-emerald-50 text-[#047857] flex items-center justify-center shrink-0">
                          <Leaf className="w-4 h-4" />
                        </span>
                        <span className="text-[13px] font-extrabold tracking-tight font-[Outfit] text-gray-900 leading-tight">
                          {s.name}
                        </span>
                      </div>
                      <div className="mt-2 text-[10px] uppercase tracking-[0.14em] font-extrabold text-gray-500">
                        {RANGE_KEYS.has(s.key) ? "Quote range" : `From $${s.min_price?.toFixed?.(0) || "—"}`}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : step === "schedule" ? (
          <div className="overflow-y-auto p-5">
            <FrequencySchedulePicker
              value={schedule}
              onChange={setSchedule}
              serviceName={selected?.name || "this service"}
              testidPrefix="addon-sched"
            />
          </div>
        ) : (
          <div className="overflow-y-auto p-5 space-y-4">
            {isAdvanced ? (
              <AdvancedAddonForm
                serviceKey={selectedKey}
                frequency={schedule.frequency}
                onQuoteChange={setAdvancedQuote}
              />
            ) : (
            <>
            {/* iter127 — Shared Yard Area picker (matches new booking design). */}
            <AreaSelector value={areas} onChange={setAreas} />

            {!isRange && (
              <div>
                <Label className="text-[11px] uppercase tracking-[0.18em] font-extrabold text-gray-500">Yard / area size (sqft)</Label>
                <Input
                  type="number"
                  min={0}
                  max={50000}
                  value={sqft}
                  onChange={(e) => setSqft(Math.max(0, Math.min(50000, parseInt(e.target.value || "0", 10) || 0)))}
                  className="h-12 mt-1.5 text-lg font-extrabold tabular-nums"
                  data-testid="add-service-sqft"
                />
                <div className="mt-2 flex gap-2">
                  {QUICK_SQFT_BUTTONS.map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setSqft(n)}
                      data-testid={`add-service-quick-${n}`}
                      className={`px-3 h-8 rounded-full text-xs font-bold border ${sqft === n ? "border-[#047857] text-[#047857] bg-emerald-50" : "border-gray-200 text-gray-600 bg-white"}`}
                    >
                      {n.toLocaleString()}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {isRange && (
              <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-3 text-[12px] text-emerald-900">
                <div className="flex items-center gap-2 font-extrabold">
                  <Sparkles className="w-3.5 h-3.5" /> Estimated range
                </div>
                <div className="mt-1 text-emerald-800/90">
                  {(RANGE_PRESETS[selectedKey]?.label) || "Standard scope"} — we&apos;ll confirm exact pricing on-site.
                </div>
              </div>
            )}

            {/* Price card */}
            <div className="rounded-2xl bg-gradient-to-br from-[#0A2E1E] to-[#062818] text-white p-5 relative overflow-hidden">
              <div className="absolute -top-8 -right-8 w-28 h-28 rounded-full bg-emerald-400/15 blur-2xl" />
              <div className="text-[10px] uppercase tracking-[0.18em] text-emerald-300 font-extrabold">Service total</div>
              <div className="mt-1 text-4xl font-extrabold tabular-nums tracking-tighter font-[Outfit]" data-testid="add-service-price">
                {loadingQuote ? <Loader2 className="w-7 h-7 animate-spin inline" /> : quote ? `$${quote.price.toFixed(2)}` : "—"}
              </div>
              {quote?.is_range && (
                <div className="mt-1 text-[12px] text-emerald-100/80">
                  Range ${quote.low?.toFixed(0)} – ${quote.high?.toFixed(0)} · midpoint shown
                </div>
              )}
              {!quote?.is_range && quote && (
                <div className="mt-1 text-[12px] text-emerald-100/80">One-time visit · {sqft.toLocaleString()} sqft</div>
              )}
            </div>
            </>
            )}
          </div>
        )}

        {/* Footer — Step 1: Continue to config */}
        {step === "schedule" && (
          <div className="border-t border-gray-100 px-5 py-3 flex items-center justify-between gap-2 bg-gray-50">
            <Button
              type="button"
              variant="ghost"
              onClick={() => { setStep("pick"); setSelectedKey(null); }}
              data-testid="add-service-schedule-back"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <Button
              type="button"
              onClick={() => setStep("config")}
              disabled={schedule.frequency === "custom" && !schedule.custom_schedule_value}
              className="rounded-full h-11 px-5 bg-[#047857] hover:bg-[#065F46] text-white font-extrabold"
              data-testid="add-service-schedule-next"
            >
              Continue to price <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        )}

        {/* Footer — Step 2: Add to booking */}
        {step === "config" && (
          <div className="border-t border-gray-100 px-5 py-3 flex items-center justify-between gap-2 bg-gray-50">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setStep("schedule")}
              data-testid="add-service-back"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <Button
              type="button"
              onClick={handleAdd}
              disabled={isAdvanced ? !advancedQuote : (!quote || loadingQuote)}
              className="rounded-full h-11 px-5 bg-[#047857] hover:bg-[#065F46] text-white font-extrabold"
              data-testid="add-service-confirm"
            >
              <CheckCircle2 className="w-4 h-4 mr-1.5" /> Add to booking
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
